import { useEffect, useState, useMemo } from "react";
import { Phone, WhatsappLogo, MapPin, FileArrowDown, UploadSimple, CalendarBlank, CheckCircle, ClipboardText, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { JOB_STATUS_LABELS, JOB_STATUS_ORDER, JOB_STATUS_COLORS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { shareWhatsApp } from "@/lib/whatsapp";
import PhotoGallery from "@/components/PhotoGallery";
import FieldData from "@/components/FieldData";

const TABS = [
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed" },
  { key: "all", label: "All" },
];

export default function MyJobs() {
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState("active");
  const [busy, setBusy] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [notePhotos, setNotePhotos] = useState({});

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addNote = async (jobId) => {
    const text = (noteDrafts[jobId] || "").trim();
    const photo = notePhotos[jobId];
    if (!text && !photo) return;
    try {
      let data;
      if (photo) {
        const fd = new FormData();
        fd.append("text", text);
        fd.append("file", photo);
        ({ data } = await api.post(`/jobs/${jobId}/notes/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        ({ data } = await api.post(`/jobs/${jobId}/notes`, { text }));
      }
      setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, notes_log: [...(j.notes_log || []), data] } : j)));
      setNoteDrafts((d) => ({ ...d, [jobId]: "" }));
      setNotePhotos((p) => ({ ...p, [jobId]: null }));
      toast.success("Note added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteNote = async (jobId, noteId) => {
    try {
      await api.delete(`/jobs/${jobId}/notes/${noteId}`);
      setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, notes_log: (j.notes_log || []).filter((n) => n.id !== noteId) } : j)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      await api.put(`/jobs/${id}`, { status });
      setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status } : j)));
      toast.success(`Marked ${JOB_STATUS_LABELS[status]}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const submitReview = async (id) => {
    setBusy(id + "review");
    try {
      await api.post(`/jobs/${id}/submit-for-review`);
      setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status: "admin_review" } : j)));
      toast.success("Sent to owner for review");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const uploadDoc = async (jobId, fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/jobs/${jobId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached"); fileEl.value = ""; load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const visible = useMemo(() => jobs.filter((j) => {
    if (tab === "active") return j.status !== "completed";
    if (tab === "completed") return j.status === "completed";
    return true;
  }), [jobs, tab, today]);

  const activeCount = jobs.filter((j) => j.status !== "completed").length;
  const overdueCount = jobs.filter((j) => j.deadline && j.deadline.slice(0, 10) < today && j.status !== "completed").length;

  return (
    <div className="space-y-5" data-testid="my-jobs">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">My Work</div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">My Jobs</h1>
        <p className="text-sm text-muted-foreground mt-1" data-testid="myjobs-summary">
          {activeCount} active{overdueCount > 0 && <span className="text-destructive font-semibold"> · {overdueCount} overdue</span>}
        </p>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`myjobs-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          <ClipboardText size={32} className="mx-auto mb-2 text-muted-foreground/50" />
          No jobs here.
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {visible.map((j) => {
            const overdue = j.deadline && j.deadline.slice(0, 10) < today && j.status !== "completed";
            const curIdx = JOB_STATUS_ORDER.indexOf(j.status);
            const mobile = (j.contact_mobile || "").trim();
            return (
              <Card key={j.id} className="p-4 space-y-3 border-border" data-testid={`myjob-${j.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-muted-foreground">{j.reference}</div>
                    <div className="font-semibold text-primary truncate">{j.applicant_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{j.property_type} · {j.property_address}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{j.bank_name}{j.branch_name ? ` — ${j.branch_name}` : ""}</div>
                  </div>
                  <span className={`shrink-0 text-[11px] px-2 py-1 rounded-full border ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
                </div>

                <div className="flex items-center gap-1.5 text-xs">
                  <CalendarBlank size={14} className={overdue ? "text-destructive" : "text-muted-foreground"} />
                  <span className={overdue ? "text-destructive font-semibold" : "text-muted-foreground"}>
                    {j.deadline ? `Due ${j.deadline.slice(0, 10)}${overdue ? " · Overdue" : ""}` : "No deadline set"}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {mobile && <a href={`tel:${mobile.replace(/[^0-9+]/g, "")}`} data-testid={`myjob-call-${j.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-muted text-primary hover:bg-muted/70 transition-colors"><Phone size={14} /> Call</a>}
                  {mobile && <button onClick={() => shareWhatsApp(mobile, `Hello, regarding valuation ${j.reference} for ${j.applicant_name}.`)} data-testid={`myjob-wa-${j.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"><WhatsappLogo size={14} /> WhatsApp</button>}
                  {j.property_location && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(j.property_location)}`} target="_blank" rel="noreferrer" data-testid={`myjob-map-${j.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-muted text-primary hover:bg-muted/70 transition-colors"><MapPin size={14} /> Maps</a>}
                </div>

                <PhotoGallery job={j} />

                <FieldData job={j} onChange={load} />

                <div className="space-y-1.5">
                  {(j.documents || []).map((d) => (
                    <a key={d.id} href={`${API}/jobs/${j.id}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-primary hover:underline" data-testid={`myjob-doc-${d.id}`}>
                      <FileArrowDown size={14} className="shrink-0" /><span className="truncate">{d.filename}</span>
                    </a>
                  ))}
                  <label className="inline-flex items-center gap-1 text-xs text-accent hover:underline cursor-pointer" data-testid={`myjob-upload-${j.id}`}>
                    <UploadSimple size={14} /> Attach document
                    <input type="file" className="hidden" onChange={(e) => uploadDoc(j.id, e.target)} accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt" />
                  </label>
                </div>

                <div>
                  <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1.5">Site Notes</div>
                  <div className="space-y-1.5 mb-2">
                    {(j.notes_log || []).map((n) => (
                      <div key={n.id} className="text-xs bg-muted rounded-md p-2" data-testid={`myjob-note-${n.id}`}>
                        {n.text && <div className="text-primary whitespace-pre-wrap">{n.text}</div>}
                        {n.photo && (
                          <a href={`${API}/jobs/${j.id}/notes/${n.id}/photo`} target="_blank" rel="noreferrer" data-testid={`myjob-note-photo-view-${n.id}`}>
                            <img src={`${API}/jobs/${j.id}/notes/${n.id}/photo`} alt="site" className="mt-1 rounded-md max-h-44 border border-border" />
                          </a>
                        )}
                        <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
                          <span>{n.by_name} · {new Date(n.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                          <button onClick={() => deleteNote(j.id, n.id)} className="text-destructive hover:underline" data-testid={`myjob-note-del-${n.id}`}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 items-center">
                    <Input value={noteDrafts[j.id] || ""} onChange={(e) => setNoteDrafts((d) => ({ ...d, [j.id]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(j.id); } }} placeholder="Add a site note…" className="text-xs h-9" data-testid={`myjob-note-input-${j.id}`} />
                    <label className="shrink-0 cursor-pointer h-9 w-9 rounded-full bg-muted flex items-center justify-center text-primary hover:bg-muted/70 transition-colors" title="Attach photo" data-testid={`myjob-note-photo-${j.id}`}>
                      <Camera size={16} />
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setNotePhotos((p) => ({ ...p, [j.id]: e.target.files?.[0] || null }))} />
                    </label>
                    <Button size="sm" variant="outline" className="h-9 rounded-full text-xs shrink-0" onClick={() => addNote(j.id)} data-testid={`myjob-note-add-${j.id}`}>Add</Button>
                  </div>
                  {notePhotos[j.id] && <div className="text-[10px] text-accent mt-1 inline-flex items-center gap-1" data-testid={`myjob-note-photo-name-${j.id}`}><Camera size={11} /> {notePhotos[j.id].name} attached</div>}
                </div>

                {JOB_STATUS_ORDER.indexOf(j.status) >= JOB_STATUS_ORDER.indexOf("admin_review") ? (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5 flex items-center gap-2 text-xs text-indigo-800" data-testid={`myjob-sent-lock-${j.id}`}>
                    <CheckCircle size={16} weight="fill" className="shrink-0 text-indigo-600" />
                    <span><b>Sent for review</b> — this report is with the owner for sign-off and is locked for editing until released.</span>
                  </div>
                ) : (
                <div>
                  <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1.5">Update status</div>
                  <div className="flex flex-wrap gap-1.5">
                    {JOB_STATUS_ORDER.map((s, i) => {
                      const done = i < curIdx; const active = i === curIdx;
                      return (
                        <button key={s} disabled={active || busy === j.id + s} onClick={() => setStatus(j.id, s)} data-testid={`myjob-status-${j.id}-${s}`}
                          className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full border transition-colors ${active ? "bg-accent text-accent-foreground border-accent font-semibold" : done ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-muted-foreground border-border hover:border-primary hover:text-primary"}`}>
                          {done && <CheckCircle size={12} weight="fill" />}{JOB_STATUS_LABELS[s]}
                        </button>
                      );
                    })}
                  </div>
                  {curIdx < JOB_STATUS_ORDER.length - 1 && JOB_STATUS_ORDER[curIdx + 1] !== "admin_review" && (
                    <Button size="sm" onClick={() => setStatus(j.id, JOB_STATUS_ORDER[curIdx + 1])} data-testid={`myjob-advance-${j.id}`} className="mt-2.5 w-full rounded-full bg-primary hover:bg-[#113A64] text-xs">
                      Mark as {JOB_STATUS_LABELS[JOB_STATUS_ORDER[curIdx + 1]]}
                    </Button>
                  )}
                  {j.status === "draft_prepared" && (
                    <Button size="sm" variant="outline" onClick={() => submitReview(j.id)} disabled={busy === j.id + "review"} data-testid={`myjob-submit-review-${j.id}`} className="mt-2 w-full rounded-full text-xs border-primary text-primary hover:bg-primary/5 hover:text-primary">
                      Send completed report to owner
                    </Button>
                  )}
                </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
